import React, { useState } from "react";
import {
  Grid,
  TextField,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  Button,
  Card,
  CardContent,
  Typography,
  Container,
  CssBaseline,
  IconButton,
  Stack,
  FormHelperText,
  AppBar,
  Toolbar,
  Tabs,
  Tab,
  Box,
  Select as MuiSelect,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import PetsIcon from "@mui/icons-material/Pets";
import EventIcon from "@mui/icons-material/Event";
import WarningIcon from "@mui/icons-material/Warning";
import LanguageIcon from "@mui/icons-material/Language";
import MenuIcon from "@mui/icons-material/Menu";
import { useTranslation } from "react-i18next";
import PregnancyTracker from "./PregnancyTracker";
import CattleEventsForm from "./CattleEventsForm";

export default function App() {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [activeTab, setActiveTab] = useState(0);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [animalType, setAnimalType] = useState("");
  const [breed, setBreed] = useState("");
  const [tagNumber, setTagNumber] = useState("");
  const [nickname, setNickname] = useState("");
  const [deliveryDate, setDeliveryDate] = useState("");

  const [cattleList, setCattleList] = useState([
    { id: 1, animalType: "cow", breed: "hf", tagNumber: "COW001", nickname: "Bella", deliveryDate: "2023-01-15" },
    { id: 2, animalType: "cow", breed: "jersey", tagNumber: "COW002", nickname: "Molly", deliveryDate: "2023-02-20" },
    { id: 3, animalType: "buffalo", breed: "murrah", tagNumber: "BUF001", nickname: "Daisy", deliveryDate: "2023-03-10" },
    { id: 4, animalType: "cow", breed: "hf", tagNumber: "COW003", nickname: "Lucy", deliveryDate: "2023-04-05" },
    { id: 5, animalType: "cow", breed: "jersey", tagNumber: "COW004", nickname: "Maggie", deliveryDate: "2023-05-12" },
    { id: 6, animalType: "buffalo", breed: "murrah", tagNumber: "BUF002", nickname: "Nelly", deliveryDate: "2023-06-18" },
    { id: 7, animalType: "cow", breed: "hf", tagNumber: "COW005", nickname: "Sophie", deliveryDate: "2023-07-22" },
    { id: 8, animalType: "cow", breed: "jersey", tagNumber: "COW006", nickname: "Annie", deliveryDate: "2023-08-30" },
    { id: 9, animalType: "buffalo", breed: "murrah", tagNumber: "BUF003", nickname: "Betty", deliveryDate: "2023-09-14" },
    { id: 10, animalType: "cow", breed: "hf", tagNumber: "COW007", nickname: "Cathy", deliveryDate: "2023-10-08" }
  ]);
  const [nextId, setNextId] = useState(11);
  const [editingId, setEditingId] = useState(null);

  const [errors, setErrors] = useState({});

  const resetForm = () => {
    setAnimalType("");
    setBreed("");
    setTagNumber("");
    setNickname("");
    setDeliveryDate("");
    setEditingId(null);
    setErrors({});
  };

  // ✅ VALIDATION: require animalType, breed, tagNumber, deliveryDate
  const validate = () => {
    const temp = {};
    if (!animalType) temp.animalType = t("cattleForm.animalTypeRequired");
    if (!breed) temp.breed = t("cattleForm.breedRequired");
    if (!tagNumber) temp.tagNumber = t("cattleForm.tagNumberRequired");
    if (!deliveryDate) temp.deliveryDate = t("cattleForm.deliveryDateRequired");
    setErrors(temp);
    return Object.keys(temp).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validate()) return;

    const payload = { id: editingId ?? nextId, animalType, breed, tagNumber, nickname, deliveryDate };

    if (editingId) {
      setCattleList((prev) => prev.map((c) => (c.id === editingId ? payload : c)));
    } else {
      setCattleList((prev) => [...prev, payload]);
      setNextId((n) => n + 1);
    }

    resetForm();
  };

  const handleEdit = (row) => {
    setEditingId(row.id);
    setAnimalType(row.animalType?.toLowerCase() || "");
    setBreed(row.breed || "");
    setTagNumber(row.tagNumber || "");
    setNickname(row.nickname || "");
    setDeliveryDate(row.deliveryDate || "");
  };

  const handleDelete = (id) => {
    setCattleList((prev) => prev.filter((c) => c.id !== id));
    if (editingId === id) resetForm();
  };

  // DataGrid columns
  const columns = [
    { field: "serial", headerName: "#", width: 70 },
    { field: "tagNumber", headerName: t("cattleForm.tagNumber"), flex: 1, minWidth: 120 },
    { field: "nickname", headerName: t("cattleForm.nickname"), flex: 1, minWidth: 120 },
    { field: "animalType", headerName: t("cattleForm.animalType"), flex: 1, minWidth: 120 },
    { field: "breed", headerName: t("cattleForm.breed"), flex: 1, minWidth: 120 },
    { field: "deliveryDate", headerName: t("cattleForm.deliveryDate"), flex: 1, minWidth: 140 },
    {
      field: "actions",
      headerName: t("actions.edit"),
      width: 140,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Stack direction="row" spacing={1}>
          <IconButton color="primary" size="small" onClick={() => handleEdit(params.row)} aria-label="edit">
            <EditIcon fontSize="small" />
          </IconButton>
          <IconButton color="error" size="small" onClick={() => handleDelete(params.row.id)} aria-label="delete">
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Stack>
      ),
    },
  ];

  // Transform list to rows for DataGrid
  const rows = cattleList.map((c, idx) => ({
    id: c.id,
    serial: idx + 1,
    tagNumber: c.tagNumber,
    nickname: c.nickname,
    animalType: c.animalType ? c.animalType.toUpperCase() : "",
    breed: c.breed,
    deliveryDate: c.deliveryDate,
  }));

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handleDrawerToggle = () => {
    setDrawerOpen(!drawerOpen);
  };

  const handleDrawerItemClick = (index) => {
    setActiveTab(index);
    setDrawerOpen(false);
  };

  const handleLanguageChange = (event) => {
    i18n.changeLanguage(event.target.value);
  };

  const renderCattleForm = () => (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Card elevation={3}>
        <CardContent>
          <Typography variant="h5" gutterBottom>
            {editingId ? t("cattleForm.editCattle") : t("cattleForm.addCattle")}
          </Typography>
            <form onSubmit={handleSubmit}>
              <Grid container spacing={2}>
                {/* Animal Type */}
                <Grid item xs={12} sm={6} md={6}>
                  <FormControl fullWidth error={!!errors.animalType} sx={{ mb: 1 }}>
                    <InputLabel id="animal-type-label">{t("cattleForm.animalType")}</InputLabel>
                    <Select
                      labelId="animal-type-label"
                      id="animal-type"
                      value={animalType}
                      label={t("cattleForm.animalType")}
                      onChange={(e) => setAnimalType(e.target.value)}
                      sx={{ minWidth: 200 }}
                      MenuProps={{ PaperProps: { sx: { minWidth: 200 } } }}
                    >
                      <MenuItem value="cow">{t("cattleForm.animalTypes.cow")}</MenuItem>
                      <MenuItem value="buffalo">{t("cattleForm.animalTypes.buffalo")}</MenuItem>
                    </Select>
                    {errors.animalType && <FormHelperText>{errors.animalType}</FormHelperText>}
                  </FormControl>
                </Grid>

                {/* Breed */}
                <Grid item xs={12} sm={6} md={6}>
                  <FormControl fullWidth error={!!errors.breed} sx={{ mb: 1 }}>
                    <InputLabel id="breed-label">{t("cattleForm.breed")}</InputLabel>
                    <Select
                      labelId="breed-label"
                      id="breed"
                      value={breed}
                      label={t("cattleForm.breed")}
                      onChange={(e) => setBreed(e.target.value)}
                      sx={{ minWidth: 200 }}
                      MenuProps={{ PaperProps: { sx: { minWidth: 200 } } }}
                    >
                      <MenuItem value="hf">{t("cattleForm.breeds.hf")}</MenuItem>
                      <MenuItem value="jersey">{t("cattleForm.breeds.jersey")}</MenuItem>
                      <MenuItem value="murrah">{t("cattleForm.breeds.murrah")}</MenuItem>
                    </Select>
                    {errors.breed && <FormHelperText>{errors.breed}</FormHelperText>}
                  </FormControl>
                </Grid>
                {/* Tag Number */}
                <Grid item xs={12} sm={6} md={4}>
                  <TextField
                    fullWidth
                    label={t("cattleForm.tagNumber")}
                    value={tagNumber}
                    onChange={(e) => setTagNumber(e.target.value)}
                    error={!!errors.tagNumber}
                    helperText={errors.tagNumber}
                    sx={{ mb: 1 }}
                  />
                </Grid>

                {/* Nickname */}
                <Grid item xs={12} sm={6} md={6}>
                  <TextField
                    fullWidth
                    label={t("cattleForm.nickname")}
                    value={nickname}
                    onChange={(e) => setNickname(e.target.value)}
                    sx={{ mb: 1 }}
                  />
                </Grid>

                {/* Delivery Date (native) */}
                <Grid item xs={12} sm={6} md={6}>
                  <TextField
                    fullWidth
                    label={t("cattleForm.deliveryDate")}
                    type="date"
                    InputLabelProps={{ shrink: true }}
                    value={deliveryDate}
                    onChange={(e) => setDeliveryDate(e.target.value)}
                    error={!!errors.deliveryDate}
                    helperText={errors.deliveryDate}
                    sx={{ mb: 2 }}
                  />
                </Grid>

                {/* Buttons */}
                <Grid item xs={12}>
                  <Box sx={{
                    display: 'flex',
                    flexDirection: { xs: 'column', sm: 'row' },
                    gap: 2,
                    mt: 2
                  }}>
                    <Button
                      variant="contained"
                      color="primary"
                      type="submit"
                      size="large"
                      sx={{
                        flex: editingId ? 1 : 'auto',
                        minHeight: 48,
                        fontSize: '1rem',
                        fontWeight: 'bold'
                      }}
                    >
                      {editingId ? t("cattleForm.updateCattle") : t("cattleForm.saveCattle")}
                    </Button>
                    {editingId && (
                      <Button
                        variant="outlined"
                        color="inherit"
                        size="large"
                        onClick={resetForm}
                        sx={{
                          flex: 1,
                          minHeight: 48,
                          fontSize: '1rem'
                        }}
                      >
                        {t("cattleForm.cancelEdit")}
                      </Button>
                    )}
                  </Box>
                </Grid>
              </Grid>
            </form>
        </CardContent>
      </Card>

      {/* Cattle DataGrid */}
      {rows.length > 0 && (
        <Card elevation={2} sx={{ mt: 4 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Saved Cattle
            </Typography>
            <Box sx={{ width: "100%", overflowX: 'auto' }}>
              <DataGrid
                rows={rows}
                columns={columns}
                autoHeight
                disableRowSelectionOnClick
                initialState={{
                  pagination: { paginationModel: { pageSize: 5, page: 0 } },
                }}
                pageSizeOptions={[5, 10, 25]}
                sx={{
                  minWidth: { xs: 400, sm: 600, md: 800 },
                  '& .MuiDataGrid-columnHeaders': {
                    fontSize: { xs: '0.7rem', sm: '0.9rem' },
                  },
                  '& .MuiDataGrid-cell': {
                    fontSize: { xs: '0.7rem', sm: '0.85rem' },
                    padding: { xs: '4px 6px', sm: '8px 16px' },
                  },
                  '& .MuiDataGrid-columnHeaderTitle': {
                    fontWeight: 'bold',
                  },
                }}
              />
            </Box>
          </CardContent>
        </Card>
      )}
    </Container>
  );

  return (
    <>
      <CssBaseline />
      <AppBar
        position="static"
        elevation={3}
        sx={{
          bgcolor: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          boxShadow: '0 8px 32px rgba(102, 126, 234, 0.3)'
        }}
      >
        <Toolbar>
          {isMobile && (
            <IconButton
              color="inherit"
              aria-label="open drawer"
              edge="start"
              onClick={handleDrawerToggle}
              sx={{ mr: 2 }}
            >
              <MenuIcon />
            </IconButton>
          )}
          <Typography variant="h6" sx={{ flexGrow: 1, fontWeight: 'bold', color: 'white' }}>
            {t("appTitle")}
          </Typography>
          <FormControl size="small" sx={{ mr: 2 }}>
            <InputLabel sx={{ color: 'white' }}>
              <LanguageIcon sx={{ mr: 1 }} />
              Language
            </InputLabel>
            <MuiSelect
              value={i18n.language}
              label="Language"
              onChange={handleLanguageChange}
              sx={{
                color: 'white',
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'rgba(255, 255, 255, 0.5)',
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'white',
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'white',
                },
              }}
            >
              <MenuItem value="en">English</MenuItem>
              <MenuItem value="mr">मराठी</MenuItem>
            </MuiSelect>
          </FormControl>
          {!isMobile && (
            <Tabs
              value={activeTab}
              onChange={handleTabChange}
              sx={{
                '& .MuiTab-root': {
                  color: 'rgba(255, 255, 255, 0.7)',
                  '&.Mui-selected': {
                    color: 'white',
                    fontWeight: 'bold'
                  }
                },
                '& .MuiTabs-indicator': {
                  backgroundColor: 'white'
                }
              }}
            >
              <Tab label={t("tabs.cattleManagement")} icon={<PetsIcon />} iconPosition="start" />
              <Tab label={t("tabs.pregnancyTracker")} icon={<EventIcon />} iconPosition="start" />
              <Tab label={t("tabs.cattleEvents")} icon={<WarningIcon />} iconPosition="start" />
            </Tabs>
          )}
        </Toolbar>
      </AppBar>

      {/* Mobile Drawer */}
      <Drawer
        anchor="left"
        open={drawerOpen}
        onClose={handleDrawerToggle}
        sx={{
          '& .MuiDrawer-paper': {
            width: 280,
            bgcolor: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: 'white',
          },
        }}
      >
        <List sx={{ pt: 2 }}>
          <ListItem disablePadding>
            <ListItemButton
              selected={activeTab === 0}
              onClick={() => handleDrawerItemClick(0)}
              sx={{
                '&.Mui-selected': {
                  bgcolor: 'rgba(255, 255, 255, 0.2)',
                  '&:hover': {
                    bgcolor: 'rgba(255, 255, 255, 0.3)',
                  },
                },
                '&:hover': {
                  bgcolor: 'rgba(255, 255, 255, 0.1)',
                },
              }}
            >
              <ListItemIcon sx={{ color: 'white' }}>
                <PetsIcon />
              </ListItemIcon>
              <ListItemText primary={t("tabs.cattleManagement")} />
            </ListItemButton>
          </ListItem>
          <ListItem disablePadding>
            <ListItemButton
              selected={activeTab === 1}
              onClick={() => handleDrawerItemClick(1)}
              sx={{
                '&.Mui-selected': {
                  bgcolor: 'rgba(255, 255, 255, 0.2)',
                  '&:hover': {
                    bgcolor: 'rgba(255, 255, 255, 0.3)',
                  },
                },
                '&:hover': {
                  bgcolor: 'rgba(255, 255, 255, 0.1)',
                },
              }}
            >
              <ListItemIcon sx={{ color: 'white' }}>
                <EventIcon />
              </ListItemIcon>
              <ListItemText primary={t("tabs.pregnancyTracker")} />
            </ListItemButton>
          </ListItem>
          <ListItem disablePadding>
            <ListItemButton
              selected={activeTab === 2}
              onClick={() => handleDrawerItemClick(2)}
              sx={{
                '&.Mui-selected': {
                  bgcolor: 'rgba(255, 255, 255, 0.2)',
                  '&:hover': {
                    bgcolor: 'rgba(255, 255, 255, 0.3)',
                  },
                },
                '&:hover': {
                  bgcolor: 'rgba(255, 255, 255, 0.1)',
                },
              }}
            >
              <ListItemIcon sx={{ color: 'white' }}>
                <WarningIcon />
              </ListItemIcon>
              <ListItemText primary={t("tabs.cattleEvents")} />
            </ListItemButton>
          </ListItem>
        </List>
      </Drawer>

      <Box sx={{
        bgcolor: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
        background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
        minHeight: '100vh',
        py: 2
      }}>
        {activeTab === 0 && renderCattleForm()}
        {activeTab === 1 && (
          <PregnancyTracker
            cattleList={cattleList}
            onNavigateToCattle={() => setActiveTab(0)}
            onNavigateToEvents={() => setActiveTab(2)}
          />
        )}
        {activeTab === 2 && (
          <CattleEventsForm
            onNavigateToCattle={() => setActiveTab(0)}
            onNavigateToPregnancy={() => setActiveTab(1)}
          />
        )}
      </Box>
    </>
  );
}
